/**
 * Phase 7a — Bloc Settings "Relances automatiques".
 *
 * - Toggle relances devis (défaut ON).
 * - Toggle relances factures (défaut OFF, opt-in, avec avertissement).
 * - Bouton "Voir un exemple" qui appelle POST /reminders/preview.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, Eye, Info, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/api";

export default function RemindersSettingsBlock({ form, setForm, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState("quote");
  const [previewSeq, setPreviewSeq] = useState(1);

  const saveField = async (field, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/company/settings", { [field]: value });
      setForm({ ...form, [field]: value });
      toast.success(field === "quote_reminders_enabled"
        ? `Relances devis ${value ? "activées" : "désactivées"}`
        : `Relances factures ${value ? "activées" : "désactivées"}`);
      onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openPreview = async (type, seq = 1) => {
    setPreviewType(type); setPreviewSeq(seq); setPreviewOpen(true);
    setPreviewLoading(true); setPreviewData(null);
    try {
      const { data } = await api.post("/reminders/preview", { target_type: type, sequence_index: seq });
      setPreviewData(data);
    } catch (e) { toast.error(formatApiError(e)); setPreviewOpen(false); }
    finally { setPreviewLoading(false); }
  };

  return (
    <>
      <Card
        id="reminders"
        className="scroll-mt-24 border-slate-800 bg-slate-900/40"
        data-testid="reminders-settings-card"
      >
        <CardContent className="space-y-5 p-6">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-5 w-5 text-[#C4553B]" />
            <div>
              <h3 className="font-semibold text-slate-100">Relances automatiques par IA</h3>
              <p className="text-xs text-slate-400">
                Génère et envoie automatiquement des rappels courtois, dans la voix de votre entreprise
                (utilise votre texte de présentation). Les corps envoyés sont archivés pour la traçabilité.
              </p>
            </div>
          </div>

          {/* Toggle DEVIS */}
          <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <Label className="text-slate-200 font-semibold">Relances devis non signés</Label>
                <p className="mt-1 text-xs text-slate-400">
                  Séquence <b>J+3 · J+7 · J+14</b> après envoi. Arrêt automatique dès signature, refus, ou expiration.
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Vous pouvez aussi désactiver au cas par cas depuis chaque devis.
                </p>
              </div>
              <Switch
                checked={!!form.quote_reminders_enabled}
                onCheckedChange={(v) => saveField("quote_reminders_enabled", v)}
                disabled={saving}
                data-testid="quote-reminders-toggle"
              />
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => openPreview("quote", 1)}
              className="mt-3 border-slate-700 bg-slate-800/60 text-slate-200"
              data-testid="preview-quote-reminder-btn"
            >
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Voir un exemple (J+3)
            </Button>
          </div>

          {/* Toggle FACTURES avec avertissement */}
          <div className="rounded-lg border border-amber-800/50 bg-amber-950/10 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <Label className="text-slate-200 font-semibold flex items-center gap-2">
                  Relances factures échues
                  <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase text-amber-300 font-semibold">
                    opt-in
                  </span>
                </Label>
                <p className="mt-1 text-xs text-slate-400">
                  Séquence <b>J+7 · J+14 · J+21</b> après échéance. Ton «&nbsp;ferme mais courtois&nbsp;», garde-fous stricts (jamais de menace,
                  jamais de mention «&nbsp;recouvrement&nbsp;», «&nbsp;huissier&nbsp;», «&nbsp;pénalités&nbsp;»).
                </p>
                <div className="mt-2 flex items-start gap-1.5 rounded border border-amber-700/40 bg-amber-950/30 px-2 py-1.5">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                  <p className="text-xs text-amber-200">
                    Vérifiez le ton avec « Voir un exemple » avant d&apos;activer. Les emails partent depuis votre adresse
                    <span className="mx-1 font-semibold">reply-to</span> — assurez-vous qu&apos;elle est correcte.
                  </p>
                </div>
              </div>
              <Switch
                checked={!!form.invoice_reminders_enabled}
                onCheckedChange={(v) => saveField("invoice_reminders_enabled", v)}
                disabled={saving}
                data-testid="invoice-reminders-toggle"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => openPreview("invoice", 1)}
                className="border-slate-700 bg-slate-800/60 text-slate-200"
                data-testid="preview-invoice-reminder-btn"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" /> Voir J+7
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => openPreview("invoice", 2)}
                className="border-slate-700 bg-slate-800/60 text-slate-200"
              >
                Voir J+14
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => openPreview("invoice", 3)}
                className="border-slate-700 bg-slate-800/60 text-slate-200"
              >
                Voir J+21
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-1.5 rounded border border-slate-800/70 bg-slate-950/20 p-2">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
            <p className="text-xs text-slate-500">
              L&apos;IA reproduit fidèlement le style de votre texte de présentation entreprise
              (paramètres · Général). Si ce texte est vide, un ton neutre professionnel est utilisé par défaut.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Modal preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl border-slate-800 bg-slate-900" data-testid="reminder-preview-modal">
          <DialogHeader>
            <DialogTitle>
              Exemple de relance {previewType === "quote" ? "devis" : "facture"} — J+{previewType === "quote" ? [3, 7, 14][previewSeq - 1] : [7, 14, 21][previewSeq - 1]}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Aperçu généré à partir de vos paramètres actuels. Le vrai email sera personnalisé
              avec le prénom et les données du client concerné.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : previewData ? (
            <div className="space-y-3">
              <div className="rounded border border-slate-800 bg-slate-950/60 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Objet</div>
                <div className="mt-1 font-semibold text-slate-100" data-testid="preview-subject">{previewData.subject}</div>
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/60 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Corps</div>
                <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-slate-200" data-testid="preview-body">{previewData.body}</pre>
              </div>
              {previewData.generated_by === "fallback_static" && (
                <div className="rounded border border-amber-700/40 bg-amber-950/20 p-2 text-xs text-amber-300">
                  Note : cet aperçu utilise le modèle de secours (IA temporairement indisponible).
                  La qualité s&apos;améliorera dès que l&apos;IA sera à nouveau disponible.
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
